import { useEffect, useState } from 'react';
import { useAccount, useSigner, useContract, useNetwork, useSignTypedData } from 'wagmi';
import { avalanche, avalancheFuji } from "wagmi/chains";
import BigNumber from 'bignumber.js';
import { toast } from 'react-toastify';
import config from '../config.json';

const VALIDATE_STATUS_CODES = {
  LOADING: -1,
  READY: 0,
  VALIDATED: 1,
  DIFF_ACC: 2,
  BIOFI_DIFF: 3,
  DBC_DIFF: 4,
  DBC_NULL: 5,
}

const VALIDATE_STATUS_TEXT = [];

VALIDATE_STATUS_TEXT[VALIDATE_STATUS_CODES.LOADING] = "Loading...";
VALIDATE_STATUS_TEXT[VALIDATE_STATUS_CODES.READY] = "ok";
VALIDATE_STATUS_TEXT[VALIDATE_STATUS_CODES.VALIDATED] = "linked";
VALIDATE_STATUS_TEXT[VALIDATE_STATUS_CODES.DIFF_ACC] = "Both wallets already exist in the database, but are linked to different accounts";
VALIDATE_STATUS_TEXT[VALIDATE_STATUS_CODES.BIOFI_DIFF] = "This Avalanche wallet is already linked to a different account";
VALIDATE_STATUS_TEXT[VALIDATE_STATUS_CODES.DBC_DIFF] = "This DBChain wallet is already linked to a different account";
VALIDATE_STATUS_TEXT[VALIDATE_STATUS_CODES.DBC_NULL] = "Invalid payment id - make sure to use the link sent to you by email";


const domain = {
  name: 'UniSafeBox',
};

const types = {
  Message: [
    { name: 'message', type: 'string' },
    { name: 'biofiAddress', type: 'address' },
    { name: 'dbchainAddress', type: 'string' },
  ],
};

export function PurchaseModule({ tokenAddress, tokenABI, purchaseContractAddress, purchaseContractABI, dbchainUser }) {
  const [tokenAmount, setTokenAmount] = useState(new BigNumber("0"));
  const [userTokenBalance, setUserTokenBalance] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [packages, setPackages] = useState([]);
  const [packagesById, setPackagesById] = useState([]);

  const [allowanceOk, setAllowanceOk] = useState(false);
  const [processingTx, setProcessingTx] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);


  const [validateStatus, setValidateStatus] = useState(-1);

  const [signMessage, setSignMessage] = useState({ message: "", biofiAddress: "", dbchainAddress: "" });
  const [signedMessage, setSignedMessage] = useState("");
  const [validateStatusChange, setValidateStatusChange] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { address } = useAccount();
  const { chain } = useNetwork();

  let supportedChain;
  if (config.DEV_MODE === true) {
    supportedChain = avalancheFuji;
  } else {
    supportedChain = avalanche;
  }

  const { data: signer } = useSigner();

  const tokenContract = useContract({
    address: tokenAddress,
    abi: tokenABI,
    signerOrProvider: signer
  })

  const purchaseContract = useContract({
    address: purchaseContractAddress,
    abi: purchaseContractABI,
    signerOrProvider: signer
  })

  const signMsgLinkAccounts = useSignTypedData({
    domain,
    types,
    primaryType: 'Message',
    value: { message: signMessage.message, biofiAddress: signMessage.biofiAddress, dbchainAddress: signMessage.dbchainAddress },
    onSettled(data, error) {
      if (!error) {
        console.log(data);
        setSignedMessage(data);
      } else {
        console.error(error);
        toast.error(error);
      }
    },
  });

  async function getPackageData() {
    if (packages.length < 1 && purchaseContract.signer !== null) {
      let packageCount = await purchaseContract?.packageCount();
      packageCount = parseInt(packageCount);

      let _packages = [];
      let _packagesById = [];

      for (let i = 0; i < packageCount; i++) {
        let subscriptionPackage = await purchaseContract?.packages(i);
        if (subscriptionPackage.active === true) {
          let newSubscriptionPackage = {};
          newSubscriptionPackage.packageId = i;
          newSubscriptionPackage.name = subscriptionPackage.name;
          newSubscriptionPackage.price = subscriptionPackage.price;
          newSubscriptionPackage.priceReadable = subscriptionPackage.price.div(10 ** 6).toString();
          _packages[_packages.length] = newSubscriptionPackage;
          _packagesById[newSubscriptionPackage.packageId] = newSubscriptionPackage;
        }
      }

      let newTokenAmount = new BigNumber(_packages[selectedPackage].price.toString());
      setTokenAmount(newTokenAmount);

      setPackages(_packages);
      setPackagesById(_packagesById);
      getAllowance(newTokenAmount);
    }
  }

  async function getAllowance(newTokenAmount) {
    let thisTokenAmount = tokenAmount;
    if (typeof (newTokenAmount) !== "undefined") {
      thisTokenAmount = newTokenAmount;
    }

    let tokenAllowance = await tokenContract?.allowance(address, purchaseContractAddress);
    let allowanceBN = new BigNumber(tokenAllowance.toString());

    let isAllowanceOk = allowanceBN.isGreaterThanOrEqualTo(thisTokenAmount);
    setAllowanceOk(isAllowanceOk);

    let userBalance = await tokenContract?.balanceOf(address);
    setUserTokenBalance(userBalance.toNumber());
  }

  async function writeApprove() {
    setProcessingTx(true);
    try {
      const transaction = await tokenContract?.connect(signer).approve(purchaseContractAddress, tokenAmount.toString());

      toast("Transaction pending...");

      let waitPromise = transaction.wait();
      waitPromise.then((results) => {
        getAllowance();

        if (results.status === 1) {
          toast.success('Approval successful');
        } else if (results.status === 0) {
          toast.error('Approval failed');
        }
        setProcessingTx(false);

      });
    } catch (e) {
      toast.error("Transaction rejected");
      setProcessingTx(false);
    }
  }

  async function writePurchase() {

    setProcessingTx(true);
    try {

      const transaction = await purchaseContract?.connect(signer).purchase(selectedPackage);

      toast("Transaction pending...");

      let waitPromise = transaction.wait();
      waitPromise.then((results) => {

        if (results.status === 1) {
          toast.success('Payment successful');
          setPaymentSuccessful(true);
        } else if (results.status === 0) {
          toast.error('Payment failed');
        }
        setProcessingTx(false);

      });
    } catch (e) {
      console.error(e);
      toast.error("Transaction rejected");
      setProcessingTx(false);
    }
  }

  function purchaseOrApprove(wrapOk) {
    if (wrapOk) {
      writePurchase();
    } else {
      writeApprove();
    }
  }

  function packageChange(newValue) {
    setSelectedPackage(newValue);

    //console.log(packagesById);
    console.log(newValue);
    console.log(packagesById[newValue].price.toString());
    let newTokenAmount = new BigNumber(packagesById[newValue].price.toString());
    setTokenAmount(newTokenAmount);

    getAllowance(newTokenAmount);
  }

  useEffect(() => {
    if (signMessage.biofiAddress !== "" && signMessage.dbchainAddress !== "") {
      signMsgLinkAccounts.signTypedData();
    }
  }, [signMessage]);

  useEffect(() => {
    linkAccounts(signedMessage);
  }, [signedMessage]);

  async function prepareSignMessage() {
    if (dbchainUser !== null && dbchainUser !== '' && address !== null) {

      let messageData = {
        message: "By signing this message I agree to linking my accounts for future payments",
        biofiAddress: address,
        dbchainAddress: dbchainUser.toLowerCase()
      };
      setSignMessage(messageData);
    }

  }

  async function linkAccounts(signedMessageHash) {
    if (signedMessageHash !== "") {
      if (dbchainUser !== null && dbchainUser !== '' && address !== null) {
        let biofiAddress = address;

        console.log("Associating " + dbchainUser + " with " + biofiAddress);

        let requestUrl = 'https://usb-payment-api-test.fnvt.net/usbapi/updateUser?BiofiUserId=' + biofiAddress + '&DBChainUserId=' + dbchainUser + '&SignedMessage=' + signedMessageHash + '&APIKey=' + config.API_KEY;

        const response = await fetch(requestUrl, {
          method: 'POST',
          mode: 'cors',
          redirect: 'follow',
          referrerPolicy: 'no-referrer'
        });

        if (!response.ok) {
          toast.error("Linking accounts failed. Try again later");
          console.error("[linkAccounts] " + response.status + " code detected. Request failed.");
          return;
        }

        const jsonResponse = await response.json();
        console.log(jsonResponse.msg);

        setValidateStatusChange(true);
        toast.success("Accounts linked successfully!");
      } else {
        console.error("Unable to link accounts");
        toast.error("Unable to link accounts");
      }
    }
  }

  async function validateAccount() {
    if (dbchainUser !== null && dbchainUser !== '' && address !== null) {
      let biofiAddress = address;

      console.log("Validating " + dbchainUser + " : " + biofiAddress);

      let requestUrl = 'https://usb-payment-api-test.fnvt.net/usbapi/validateUserDetails?BiofiUserId=' + biofiAddress + '&DBChainUserId=' + dbchainUser + '&APIKey=' + config.API_KEY;

      const response = await fetch(requestUrl, {
        method: 'GET',
        mode: 'cors',
        redirect: 'follow',
        referrerPolicy: 'no-referrer'
      });

      if (!response.ok && response.status !== 400) {
        console.error("[validateAccount] " + response.status + " code detected. Request failed.");
        return;
      }

      const jsonResponse = await response.json();
      setValidateStatus(jsonResponse.data.ValidateStatus);
      setIsLoading(false);

      console.log(jsonResponse.msg);
      console.log(jsonResponse.data.ValidateStatus);
    } else {
      console.error("No address was given");
    }
  }

  useEffect(() => {
    getPackageData();
    validateAccount();
  }, [dbchainUser, address, validateStatusChange, purchaseContract]);

  return (
    <>
      {
        (chain.network === supportedChain.network) ? (
          <>
            {
              !isLoading ? (
                (validateStatus === VALIDATE_STATUS_CODES.READY) ? (
                  <>
                    <h3>Linking accounts</h3>
                    <p>
                      This is the first time you're paying for your USB subscription. You'll have to link your Avalanche wallet with your USB account.
                    </p>
                    <p>
                      To do this, make sure that the connected wallet is the one you want to use for all future payments and click the button below to sign a message.
                    </p>
                    <br />
                    <button onClick={prepareSignMessage} className="btn-purchase m-auto">Link accounts</button>
                    <br />
                  </>
                ) : (
                  (validateStatus === VALIDATE_STATUS_CODES.VALIDATED) ? (
                    paymentSuccessful ? (
                      <>
                      <h3>Payment sucessful!</h3>
                      <span>
                        Thank you for your payment. You should see the updated expiration date of your account in the app.
                      </span>
                      </>
                    ) : (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          purchaseOrApprove(allowanceOk);
                        }}
                        className="text-center"
                      >
                        <span>Select package: </span>
                        {
                          packages.length < 1 ? (
                            <select value="-1" disabled={true}>
                              <option value="-1" key="-1">Loading...</option>
                            </select>
                          ) : (
                            <select value={selectedPackage} onChange={(e) => packageChange(e.target.value)}>
                              {
                                packages.map((option) => (
                                  <option value={option.packageId} key={option.packageId}>{option.name} - {option.priceReadable} $BioFi</option>
                                ))
                              }
                            </select>
                          )
                        }

                        <button disabled={packages.length < 1 || tokenAmount.lte(0) || processingTx || tokenAmount.gt(userTokenBalance)} className={allowanceOk ? "btn-purchase" : "btn-purchase approve"}>{allowanceOk ? "Purchase" : "Approve"}</button>
                      </form >
                    )
                  ) : (
                    <>
                      <br />
                      <span>{VALIDATE_STATUS_TEXT[validateStatus]}</span>
                      <br />
                    </>
                  )
                )
              ) : (
                <span>Loading...</span>
              )
            }

          </>

        ) : (
          <span>Switch chain to Avalanche</span>
        )
      }
    </>
  );
}